import React from 'react';
import { Paperclip, X } from 'lucide-react';
import { Attachment } from '../models/Content';
import { 
  formatFileSize, 
  getFileIcon, 
  handleFileUpload as handleFileUploadUtil,
  removeAttachment as removeAttachmentUtil,
  createDragHandlers,
  FILE_VALIDATION
} from '../utils/fileUtils';

interface FileAttachmentUploadProps {
  attachments: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  label?: string;
  className?: string;
}

const FileAttachmentUpload: React.FC<FileAttachmentUploadProps> = ({
  attachments,
  setAttachments,
  label = "Attachments",
  className = ""
}) => {
  const dragHandlers = createDragHandlers(setAttachments);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileUploadUtil(e, setAttachments);
  };

  const removeAttachment = (attachmentId: string) => {
    removeAttachmentUtil(attachmentId, setAttachments);
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div 
        className="border-2 border-dashed border-gray-300 rounded-lg p-4"
        onDragOver={dragHandlers.handleDragOver}
        onDragEnter={dragHandlers.handleDragEnter}
        onDragLeave={dragHandlers.handleDragLeave}
        onDrop={dragHandlers.handleDrop}
      >
        <div className="text-center">
          <Paperclip className="mx-auto h-8 w-8 text-gray-400" />
          <div className="mt-2">
            <label htmlFor="file-upload" className="cursor-pointer">
              <span className="text-blue-600 hover:text-blue-500 font-medium">
                Click to upload files
              </span>
              <span className="text-gray-500"> or drag and drop</span>
            </label>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              multiple
              onChange={handleFileUpload}
              accept={FILE_VALIDATION.ACCEPTED_TYPES}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {FILE_VALIDATION.DESCRIPTION}
          </p>
        </div>
      </div>

      {/* Display uploaded attachments */}
      {attachments.length > 0 && (
        <div className="mt-3 space-y-2">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
              <div className="flex items-center space-x-2">
                {getFileIcon(attachment.type)}
                <div className="text-sm">
                  <div className="font-medium text-gray-900">{attachment.name}</div>
                  <div className="text-gray-500">{formatFileSize(attachment.size)}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeAttachment(attachment.id)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileAttachmentUpload; 